import pedidoVentaService, {
  PedidoVenta,
  PedidoVentaDetalle,
} from './pedidoVentaService';
import clienteService from './clienteService';
import proveedorService from './proveedorService';
import { cantidadEntregablePedido } from '../utils/cantidadesPedido';
import { elegirPorcConversion } from '../utils/pedidoConversionCantidades';

const porcClienteCache = new Map<number, number>();
const porcProveedorCache = new Map<number, number>();

async function porcCliente(nroCliente: number): Promise<number> {
  if (porcClienteCache.has(nroCliente)) return porcClienteCache.get(nroCliente) ?? 0;
  let porc = 0;
  try {
    const cliente = await clienteService.getCliente(nroCliente);
    porc = elegirPorcConversion(cliente.porcCantB, 0);
  } catch {
    porc = 0;
  }
  porcClienteCache.set(nroCliente, porc);
  return porc;
}

async function porcProveedor(nroProveedor: number): Promise<number> {
  if (porcProveedorCache.has(nroProveedor)) return porcProveedorCache.get(nroProveedor) ?? 0;
  let porc = 0;
  try {
    const proveedor = await proveedorService.getProveedor(nroProveedor);
    porc = elegirPorcConversion(0, proveedor.porcCantB);
  } catch {
    porc = 0;
  }
  porcProveedorCache.set(nroProveedor, porc);
  return porc;
}

/** % Conv. del renglón: el del cliente, o el del proveedor del artículo si el cliente no tiene. */
export async function resolverPorcConversionRenglon(
  nroCliente: number | null | undefined,
  producto: unknown,
): Promise<number> {
  const nro = Number(nroCliente ?? 0);
  const delCliente = nro > 0 ? await porcCliente(nro) : 0;
  if (delCliente > 0) return delCliente;

  const raw = producto as { codProveedor?: number | null; CodProveedor?: number | null } | null;
  const codProv = Number(raw?.codProveedor ?? raw?.CodProveedor ?? 0);
  if (!(codProv > 0)) return 0;
  return porcProveedor(codProv);
}

export type AplicarConversionMaestrosResult = {
  pedido: PedidoVenta;
  /** True si alguna línea quedó con Cantidad B > 0 tras el floor. */
  tieneCantidadB: boolean;
};

/**
 * POST conversion-automatica?modo=maestros y corrige cantidades con floor
 * (p. ej. 2 uds @ 30% → B = 0), persistiendo el pedido corregido.
 * Antes limpia % / Cant. B del renglón para que gane el % del cliente o del proveedor
 * (si el renglón ya tenía 90%, no debe quedarse con ese valor).
 * No copia a otros tenants (eso es copiar-relacionados / Enviar pedido).
 */
export async function aplicarConversionMaestrosConFloor(
  pedidoId: number,
): Promise<AplicarConversionMaestrosResult> {
  const actual = await pedidoVentaService.getPedidoVenta(pedidoId, { sinCache: true });
  const detallesLimpios: PedidoVentaDetalle[] = (actual.detalles ?? []).map((d, idx) => {
    const cant = Number(d.cantidad ?? 0);
    const cantB = Number(d.cantidadB ?? 0);
    const total = (Number.isFinite(cant) && cant > 0 ? Math.floor(cant) : 0)
      + (Number.isFinite(cantB) && cantB > 0 ? Math.floor(cantB) : 0);
    return {
      ...d,
      nroItem: d.nroItem || idx + 1,
      cantidad: total,
      cantidadB: 0,
      porcCantB: null,
      porcSubtotalCantB: null,
      subtotal: Number(d.precioUnitario ?? 0) * total,
      precioMinimo: undefined,
    };
  });
  await pedidoVentaService.updatePedidoVenta(
    pedidoId,
    { ...actual, detalles: detallesLimpios },
    { preservarPrecios: true },
  );

  let actualizado = await pedidoVentaService.conversionDesdeMaestros(pedidoId);
  if (!actualizado) {
    actualizado = await pedidoVentaService.getPedidoVenta(pedidoId, { sinCache: true });
  }

  const detalles: PedidoVentaDetalle[] = (actualizado.detalles ?? []).map((d, idx) => {
    const pct = Math.floor(Number(d.porcCantB ?? d.porcSubtotalCantB ?? 0) || 0);
    const cant = Math.trunc(Number(d.cantidad) || 0);
    const b = Math.trunc(Number(d.cantidadB) || 0);
    const bruto = cant + b;
    const entregable = cantidadEntregablePedido(bruto, d.cantidadesPES, d.cantidadCanc);
    const bFloor = pct > 0
      ? Math.min(entregable, Math.floor((entregable * Math.min(pct, 100)) / 100))
      : Math.min(b, entregable);
    // La espera y lo cancelado siguen dentro de cantidad. El picking los resta una sola vez.
    const cantidad = Math.max(0, bruto - bFloor);
    return {
      ...d,
      nroItem: d.nroItem || idx + 1,
      cantidad,
      cantidadB: bFloor,
      porcCantB: pct > 0 ? Math.min(pct, 100) : 0,
      porcSubtotalCantB: pct > 0 ? Math.min(pct, 100) : 0,
      subtotal: Number(d.precioUnitario ?? 0) * cantidad,
      precioMinimo: undefined,
    };
  });

  const importeTotal = detalles.reduce((acc, d) => {
    const sub = d.subtotal ?? Number(d.precioUnitario ?? 0) * Number(d.cantidad ?? 0);
    return acc + sub;
  }, 0);

  const corregido: PedidoVenta = {
    ...actualizado,
    detalles,
    importeTotal,
  };

  await pedidoVentaService.updatePedidoVenta(pedidoId, corregido, { preservarPrecios: true });

  return {
    pedido: corregido,
    tieneCantidadB: detalles.some((d) => Number(d.cantidadB ?? 0) > 0),
  };
}

export function pedidoTieneCantidadB(pedido: PedidoVenta | null | undefined): boolean {
  return (pedido?.detalles ?? []).some((d) => Number(d.cantidadB ?? 0) > 0);
}

export const MENSAJE_CONFIRMAR_FACTURAR_PEDIDO_CON_CONVERSION =
  'Este pedido tiene la conversión hecha (Cantidad B). Se facturará el remanente del origen. ¿Querés facturarlo igual?';

const STORAGE_FACTURAR_CONVERSION_OK = 'eone.facturarPedidoConversionOk';

/** Evita repetir el aviso si ya se confirmó en el listado antes de abrir el POS. */
export function marcarConversionFacturarConfirmada(pedidoId: number): void {
  try {
    sessionStorage.setItem(STORAGE_FACTURAR_CONVERSION_OK, String(pedidoId));
  } catch {
    /* sessionStorage no disponible */
  }
}

export function consumirConversionFacturarConfirmada(pedidoId: number): boolean {
  try {
    const v = sessionStorage.getItem(STORAGE_FACTURAR_CONVERSION_OK);
    if (v === String(pedidoId)) {
      sessionStorage.removeItem(STORAGE_FACTURAR_CONVERSION_OK);
      return true;
    }
  } catch {
    /* sessionStorage no disponible */
  }
  return false;
}

/**
 * Si el pedido tiene Cantidad B, pide confirmación antes de facturar.
 * `knownTieneCantidadB` evita un GET si el listado ya lo resolvió.
 */
export async function confirmarFacturarPedidoSiTieneConversion(
  pedidoId: number,
  options?: { knownTieneCantidadB?: boolean },
): Promise<boolean> {
  let tieneB = options?.knownTieneCantidadB;
  if (tieneB === undefined) {
    try {
      const pedido = await pedidoVentaService.getPedidoVenta(pedidoId, { sinCache: true });
      tieneB = pedidoTieneCantidadB(pedido);
    } catch {
      return true;
    }
  }
  if (!tieneB) return true;
  const ok = window.confirm(MENSAJE_CONFIRMAR_FACTURAR_PEDIDO_CON_CONVERSION);
  if (ok) marcarConversionFacturarConfirmada(pedidoId);
  return ok;
}

/** Confirmación síncrona cuando el pedido ya está en memoria (p. ej. al abrir el POS). */
export function confirmarFacturarPedidoCargadoSiTieneConversion(
  pedido: PedidoVenta,
): boolean {
  const id = Number(pedido.nroFormulario) || 0;
  if (id > 0 && consumirConversionFacturarConfirmada(id)) return true;
  if (!pedidoTieneCantidadB(pedido)) return true;
  return window.confirm(MENSAJE_CONFIRMAR_FACTURAR_PEDIDO_CON_CONVERSION);
}

/**
 * Aviso al incorporar un pedido (pendiente / último pedido) que ya tiene Cantidad B.
 * `yaConfirmados` evita repetir el diálogo por el mismo nroFormulario en la misma sesión de POS.
 */
export function confirmarIncorporarPedidoSiTieneConversion(
  tieneCantidadB: boolean,
  pedidoId?: number,
  yaConfirmados?: Set<number>,
): boolean {
  if (!tieneCantidadB) return true;
  const id = Number(pedidoId) || 0;
  if (id > 0) {
    if (consumirConversionFacturarConfirmada(id)) {
      yaConfirmados?.add(id);
      return true;
    }
    if (yaConfirmados?.has(id)) return true;
  }
  const ok = window.confirm(MENSAJE_CONFIRMAR_FACTURAR_PEDIDO_CON_CONVERSION);
  if (ok && id > 0) {
    yaConfirmados?.add(id);
    marcarConversionFacturarConfirmada(id);
  }
  return ok;
}

export function puedeConversionPedidoListado(p: PedidoVenta): boolean {
  const estado = p.estado || 'Nuevo';
  const facturado = String(p.facturado ?? '').trim().toUpperCase();
  const nroFactura = Number(p.nroFormularioFactura ?? 0);
  return (estado === 'Nuevo' || estado === 'Impreso') && facturado !== 'S' && nroFactura === 0;
}
